import { ChangeDetectionStrategy, Component, Injector } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import {
  Pagination,
  ClientSidePermissionRoleLinkContainerModel,
  app_mst_role_RowClass,
  bl_applet_client_side_perm_dfn_RowClass,
  ClientSideTenantPermissionService,
  TenantRoleService,
} from 'blg-akaun-ts-lib';
import { PermissionFacade } from '../../../facades/permission.facade';
import { PermissionSetModel } from '../../../../../models/perm-set.model';
import { ViewColumnComponent } from '../../../../../view-column.component';
import { AppConfig } from '../../../../../visa';
import { SubSink } from 'subsink2';
import { ClientSidePermissionsActions } from '../../../client-side-permissions-controller/actions'
import { ClientSidePermissionsSelectors } from '../../../client-side-permissions-controller/selectors';
import { PermissionStates } from '../../../permission-controller/root';
import { combineLatest, EMPTY, iif, of } from 'rxjs';
import { delay, mergeMap } from 'rxjs/operators';
import { TranslateService } from "@ngx-translate/core";

interface LocalState {
  deactivateReturn: boolean;
  deleteConfirmation: boolean;
}

@Component({
  selector: 'app-client-side-permission-edit',
  templateUrl: './client-side-permission-edit.component.html',
  styleUrls: ['./client-side-permission-edit.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ComponentStore]
})
export class ClientSidePermissionEditComponent extends ViewColumnComponent {

  protected readonly index = 2;
  private localState: LocalState;

  readonly localState$ = this.viewColFacade.selectLocalState(this.index);
  readonly deactivateReturn$ = this.componentStore.select(state => state.deactivateReturn);
  readonly deleteConfirmation$ = this.componentStore.select(state => state.deleteConfirmation);

  prevIndex: number;
  private prevLocalState: any;

  apiVisa = AppConfig.apiVisa;
  masterVisa = AppConfig.masterVisa;

  public form: FormGroup;

  // keep track of form being enabled/disabled
  formDisabled: boolean = false;

  // no leakage of subscribe calls
  private subSink = new SubSink;

  // fields that require options
  newRoleArr: app_mst_role_RowClass[] = [];
  roleArr: app_mst_role_RowClass[] = [];
  newClientPermArr: bl_applet_client_side_perm_dfn_RowClass[] = [];
  clientPermArr: bl_applet_client_side_perm_dfn_RowClass[] = [];

  // perm templates
  permTemplateArr = [];
  newPermTemplateArr = [];

  appletGuid: string = sessionStorage.getItem('appletGuid');
  newPermSet: PermissionSetModel;

  deleteConfirmation = false;

  //translation variable
  title: string;
  client_side_permission_text: string;
  select_permission_text: string;
  invalid_entry: string;
  back_button: string;
  role_text: string;
  select_role_text: string;
  save_button: string;
  confirmation_button: string;
  delete_button: string;

  constructor(
    private clientSidePermissionService: ClientSideTenantPermissionService,
    // private teamService: TeamService,
    private tenantRoleService: TenantRoleService,
    private fb: FormBuilder,
    private viewColFacade: PermissionFacade,
    private store: Store<PermissionStates>,
    private readonly componentStore: ComponentStore<LocalState>,
    private injector: Injector
  ) {
    super();
    const translateService = this.injector.get(TranslateService, null);
    if (translateService) {
      translateService.get('labels').subscribe(res => {
        this.title = res.edit_client_side_permission;
        this.role_text = res.role;
        this.select_role_text = res.select_role;
        this.client_side_permission_text = res.client_side_permission;
        this.select_permission_text = res.select_permission;
        this.invalid_entry = res.invalid_entry;
        this.save_button = res.save_button;
        this.back_button = res.back_button;
        this.confirmation_button = res.confirmation_button;
        this.delete_button = res.delete_button;
      });

    } else {
      this.title = 'Edit Client Side Permission';
      this.role_text = 'Role';
      this.select_role_text = 'Select Role';
      this.client_side_permission_text = 'Client Side Permission';
      this.select_permission_text = 'Select Permission';
      this.invalid_entry = 'Not a valid entry';
      this.save_button = 'SAVE';
      this.back_button = 'Back';
      this.confirmation_button = 'CLICK AGAIN TO CONFIRM';
      this.delete_button = 'DELETE';
    }
  }

  /**
   * Lifecycle method: Called when the component is first initialised.
   */
  ngOnInit(): void {
    this.subSink.sink = this.localState$.subscribe(a => {
      this.localState = a;
      this.componentStore.setState(a);
    });
    this.subSink.sink = this.viewColFacade.prevIndex$.subscribe(resolve => this.prevIndex = resolve);
    this.subSink.sink = this.viewColFacade.prevLocalState$().subscribe(resolve => this.prevLocalState = resolve);

    this.form = this.fb.group({
      role: null,
      clientPerm:null,
      currRole:null,
      currClientPerm:null,
    })
    this.subSink.sink = combineLatest([this.clientSidePermissionService.getByCriteria(
      new Pagination(
        null, 100,null,
        [
          { columnName: 'applet_guid', value: this.appletGuid },
          { columnName: 'orderBy', value: 'updated_date' },
          { columnName: 'order', value: 'DESC' },
        ]
      ),
      this.apiVisa), this.tenantRoleService.get(this.apiVisa),
      this.store.select(ClientSidePermissionsSelectors.selectEntity)]
      ).subscribe({next: ([perm, role, entity]) => {
        this.newClientPermArr = this.clientPermArr = perm.data.map(d => d.bl_applet_client_side_perm_dfn)
        this.newRoleArr = this.roleArr = role.data.map(d => d.app_mst_role)
        this.form.patchValue({
          role: entity.bl_applet_client_side_perm_to_role_link.role_guid,
          clientPerm: entity.bl_applet_client_side_perm_to_role_link.client_perm_guid
        })
      }});
    this.subSink.sink = this.deleteConfirmation$.pipe(
      mergeMap(a => {
        return iif(() => a, of(a).pipe(delay(3000)), of(EMPTY));
      })
    ).subscribe(resolve => {
      if (resolve === true) {
        this.componentStore.patchState({deleteConfirmation: false});
        this.deleteConfirmation = false;
      }
    });
  }

  /**
   * This function is called whe the user clicks on the save button.
   * This will dispatch an NgRx action to create a new permission set.
   *
   */
   onSubmit() {
    const perm = new ClientSidePermissionRoleLinkContainerModel();
    perm.bl_applet_client_side_perm_to_role_link.applet_guid = this.appletGuid;
    perm.bl_applet_client_side_perm_to_role_link.role_guid= this.form.value.role;
    perm.bl_applet_client_side_perm_to_role_link.client_perm_guid= this.form.value.clientPerm;
    this.store.dispatch(ClientSidePermissionsActions.updateClientSidePermInit({ clientSidePerm:perm }))
  }

 /**
   * Dynamically change the options of shown on the screen by applying this filter.
   *
   * @param filterValue the value that the user types onto the screen
   */
  applyRoleFilter(filterValue: string) {
    filterValue = filterValue.trim(); // remove whitespace
    filterValue = filterValue.toLowerCase();  // MatTableDataSource defaults to lowercase matches
    this.newRoleArr = this.roleArr.filter( (option) =>
    option.name.toLowerCase().includes(filterValue) );
  }

/**
   * Dynamically change the options of shown on the screen by applying this filter.
   *
   * @param filterValue the value that the user types onto the screen
   */
 applyPermFilter(filterValue: string) {
  filterValue = filterValue.trim(); // remove whitespace
  filterValue = filterValue.toLowerCase();  // MatTableDataSource defaults to lowercase matches
  this.newClientPermArr = this.clientPermArr.filter( (option) =>
  option.perm_name.toLowerCase().includes(filterValue) );
}

  onDelete() {
    if (this.deleteConfirmation) {
      this.store.dispatch(ClientSidePermissionsActions.deleteClientSidePermInit());
      this.deleteConfirmation = false;
      this.componentStore.patchState({deleteConfirmation: false});
    } else {
      this.deleteConfirmation = true;
      this.componentStore.patchState({deleteConfirmation: true});
    }
  }

  /**
   * Return to the previous drawer/view column.
   */
   onReturn() {
    this.viewColFacade.updateInstance(this.prevIndex, {
      ...this.prevLocalState,
      deactivateAdd: false,
      deactivateList: false
    });
    this.viewColFacade.onPrev(this.prevIndex);
  }

  /**
   * Lifecycle method: Called when this component is destroyed.
   */
   ngOnDestroy() {
    this.viewColFacade.updateInstance(this.index, {
      ...this.localState,
    });
    this.subSink.unsubscribe();
  }

}
